/**
 * CaptionOverlayControl — the shared "Text overlay" editor section (deck N40/N41:
 * "One Text overlay component — the identical control styles Container title
 * bars"). Bucket D / wave D2 / DL-037 (deck
 * concepts/builder-blocks/11-image-caption-overlay-editor).
 *
 * REUSE, NOT DUPLICATION: every control is the app's existing WL-011 primitive —
 * the same TextInputField / PosGrid (the 9-spot 3×3 grid) / SegChoice / LabeledSlider
 * used across the block editors. This section owns no server state; it is fully
 * controlled (`value` = the caption overlay object, `onChange(nextOverlay)`), and
 * the live preview + AA guard live in the pure captionOverlay model so what the
 * builder shows is what the viewer renders.
 *
 * A11y: PosGrid is a real ARIA radiogroup; SegChoice is a labelled segmented
 * control; the slider is keyboard reachable. No motion. Desktop targets per the
 * WL-011 controls.
 */

import React from "react";
import {
  TextInputField,
  PosGrid,
  SegChoice,
  LabeledSlider,
} from "../../BlockEditorPane/controls";
import {
  CAPTION_SCOPES,
  CAPTION_SCOPE_LABEL,
  CAPTION_TONES,
  CAPTION_TONE_LABEL,
  defaultCaptionOverlay,
} from "./captionOverlay";

const CaptionOverlayControl = ({ value, onChange = () => {} }) => {
  const v = { ...defaultCaptionOverlay(), ...(value || {}) };
  const patch = (p) => onChange({ ...v, ...p });
  const darken = v.amount < 0;

  return (
    <div data-caption-overlay-control>
      {/* QA-63 (Kelly, 2026-08-03): "the image panel has two different things
          both called caption." She was right — this field and the image's own
          caption field both read exactly "Caption" in the same panel, which is
          also two identically-named controls for a screen reader. This one is
          the text PAINTED ON the image (the N40 overlay); the other is the
          image's caption/alt. Naming it for what it is removes the collision
          without renaming the field users already know. */}
      <TextInputField
        label="Overlay text"
        value={v.text}
        counterMax={80}
        maxLength={80}
        onChange={(t) => patch({ text: t })}
        placeholder="Add text over the image"
      />

      <PosGrid label="Position" value={v.position} onChange={(p) => patch({ position: p })} />

      <SegChoice
        label="Overlay"
        value={v.scope}
        options={CAPTION_SCOPES.map((s) => ({ label: CAPTION_SCOPE_LABEL[s], value: s }))}
        onChange={(s) => patch({ scope: s })}
      />

      {v.scope !== "none" && (
        <LabeledSlider
          label={darken ? "Darken" : "Lighten"}
          value={Math.abs(v.amount)}
          min={0}
          max={100}
          unit="%"
          onChange={(n) => patch({ amount: darken ? -n : n })}
        />
      )}

      {v.scope !== "none" && (
        <SegChoice
          label="Overlay direction"
          value={darken ? "darken" : "lighten"}
          options={[
            { label: "Darken", value: "darken" },
            { label: "Lighten", value: "lighten" },
          ]}
          onChange={(dir) =>
            patch({ amount: dir === "darken" ? -Math.abs(v.amount || 40) : Math.abs(v.amount || 40) })
          }
        />
      )}

      <SegChoice
        label="Text tone"
        value={v.tone}
        options={CAPTION_TONES.map((t) => ({ label: CAPTION_TONE_LABEL[t], value: t }))}
        onChange={(t) => patch({ tone: t })}
      />
    </div>
  );
};

export default CaptionOverlayControl;
